import moment from "moment-timezone";
import Form from "rc-field-form";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Label } from "reactstrap";
import Button from "../../../../../components/Button";
import Field from "../../../../../components/Field";
import CustomTimeInput from "../../../../../components/Inputs/CustomTimeInput";
import DateInput from "../../../../../components/Inputs/DateInput";
import Modal from "../../../../../components/Modal";
import { applyTimezone, combineDateAndTime } from "../../../../../utils/helpers/date";

function MissPunchModal({ visible, close, values, submit }) {
    const { t } = useTranslation();
    // which side the employee is being asked to supply
    const isMissingIn = values?.missPunchSide === "start";
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const onCancel = useCallback(() => {
        form.resetFields();
        close();
    }, [form, close]);

    const onFinish = useCallback(async ({ startDate: startInput, endDate: endInput, note }) => {
        const { date, startDate, endDate, timezone, job, location } = values;
        const data = {
            timesheetId: values?.id,
            date: date,
            timezone: timezone,
            job,
            location,
            note,
        };
        if (isMissingIn) {
            // the clock-out is the known side here; the request supplies the clock-in
            let start = combineDateAndTime(moment(date), moment(startInput), timezone);
            if (moment(start).isAfter(moment(endDate), "seconds")) {
                start = moment(start).subtract(1, "day");
                start = applyTimezone(moment(start), values?.timezone, true);
            }
            data.startDate = start.toISOString(true);
            data.endDate = endDate;
        } else {
            let end = combineDateAndTime(moment(date), moment(endInput), timezone);
            if (moment(end).isBefore(moment(startDate), "seconds")) {
                end = moment(end).add(1, "day");
                end = applyTimezone(moment(end), values?.timezone, true);
            }
            data.startDate = startDate;
            data.endDate = end.toISOString(true);
        }
        setLoading(true);
        try {
            await submit(values?.id, data);
            onCancel();
        } catch (err) {
        } finally {
            setLoading(false);
        }
    }, [submit, values, onCancel, isMissingIn]);

    return (
        <Modal
            title={t("create-miss-punch-request")}
            isOpen={visible}
            close={onCancel}
            toggle={onCancel}
            centered
        >
            <Form form={form} onFinish={onFinish}>
                <div className="mb-3">
                    <Label>{t("date")}</Label>
                    <DateInput disabled={true} value={moment(values?.date).toDate()} />
                </div>

                <div className="mb-3">
                    <Label>{isMissingIn ? t("end-time") : t("start-time")}</Label>
                    <CustomTimeInput
                        value={moment(isMissingIn ? values?.endDate : values?.startDate).parseZone()}
                        disabled={true}
                    />
                </div>

                {isMissingIn ? (
                    <Field
                        label={t("start-time")}
                        name="startDate"
                        rules={[
                            {
                                required: true,
                                message: t("required-start-time")
                            },
                            {
                                validator: (_, value) => {
                                    if (value) {
                                        const { date, endDate, timezone } = values;
                                        const start = combineDateAndTime(moment(date), moment(value), timezone);
                                        if (moment(start).isSame(moment(endDate), "seconds")) {
                                            return Promise.reject(
                                                new Error(t("end-date-bigger-than-start-date"))
                                            );
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <CustomTimeInput />
                    </Field>
                ) : (
                    <Field
                        label={t("end-time")}
                        name="endDate"
                        rules={[
                            {
                                required: true,
                                message: t("required-end-time")
                            },
                            {
                                validator: (_, value) => {
                                    if (value) {
                                        const { date, startDate, timezone } = values;
                                        let end = combineDateAndTime(moment(date), moment(value), timezone);
                                        if (moment(end).isSame(moment(startDate), "seconds")) {
                                            return Promise.reject(
                                                new Error(t("end-date-bigger-than-start-date"))
                                            );
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <CustomTimeInput />
                    </Field>
                )}

                <Field label={t("note")} name="note">
                    <Input
                        resize="none"
                        rows="7"
                        type="textarea"
                        className="h-100"
                        placeholder={t("note")}
                    />
                </Field>

                <div className="d-flex justify-content-end">
                    <Button
                        type="button"
                        color="muted"
                        disabled={loading}
                        onClick={onCancel}
                    >
                        {t("cancel")}
                    </Button>
                    <Button color="primary" loading={loading} disabled={loading}>
                        {t("save")}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

export default MissPunchModal;
